/**
 * Live Google rating of a sede (spec §12.3), from Places API (New) Place
 * Details:
 *
 *   GET https://places.googleapis.com/v1/places/{placeId}
 *   X-Goog-Api-Key: <key>
 *   X-Goog-FieldMask: rating,userRatingCount
 *   → { "rating": 4.9, "userRatingCount": 30 }
 *
 * The key is a browser key restricted by HTTP referrer to the sede domains
 * (and to the Places API), passed to the build as VITE_GOOGLE_PLACES_KEY —
 * it ends up in the public bundle by design. For a deploy build put it in
 * landings/.env.production.local (git-ignored); see landings/.env.example.
 * The referrer check stops other websites, not scripts that fake the header:
 * cap the daily requests of Places API (New) in the key's project (Quotas)
 * and set a budget alert on the billing account (Billing → Budgets & alerts).
 * No key, or no place id from the backend → no request, null.
 *
 * EEA Maps Platform terms: `rating` and `userRatingCount` may not be cached
 * or stored. This module keeps nothing — no module state, no storage; the
 * caller holds the values in component state for the page view only and
 * asks again on the next one. Billing: each call is one Place Details
 * (Enterprise SKU) request, so callers fetch once per page view, and only
 * when the figure is about to be seen.
 *
 * Never throws: any failure (network, HTTP error, quota, bad key, missing
 * field) resolves to null and the caller shows no number.
 * CSP: needs `connect-src https://places.googleapis.com` (nginx.conf.example).
 */
import { useEffect, useState } from 'react'

export interface GoogleRating {
  /** 1–5, as Google reports it (one decimal). */
  rating: number
  /** Number of ratings behind it. */
  count: number
}

const ENDPOINT = 'https://places.googleapis.com/v1/places/'

function envKey(): string {
  const k = import.meta.env.VITE_GOOGLE_PLACES_KEY
  return typeof k === 'string' ? k.trim() : ''
}

export async function fetchGoogleRating(
  placeId: string,
  key: string = envKey(),
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleRating | null> {
  if (!placeId || !key) return null
  try {
    const res = await fetchImpl(ENDPOINT + encodeURIComponent(placeId), {
      method: 'GET',
      credentials: 'omit',
      headers: { 'X-Goog-Api-Key': key, 'X-Goog-FieldMask': 'rating,userRatingCount' },
    })
    if (!res.ok) return null
    const body = (await res.json()) as { rating?: unknown; userRatingCount?: unknown }
    const rating = body?.rating
    const count = body?.userRatingCount
    if (typeof rating !== 'number' || !(rating >= 1 && rating <= 5)) return null
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 1) return null
    return { rating, count }
  } catch {
    return null
  }
}

/**
 * The rating for this page view: null until it arrives, and for good on any
 * failure. Fetches once per mount, and only once `enabled` turns true (e.g.
 * when the laurel scrolls into view) and a place id is known.
 */
export function useGoogleRating(placeId: string | undefined, enabled = true): GoogleRating | null {
  const [value, setValue] = useState<GoogleRating | null>(null)
  // `enabled` only ever goes false → true and the place id does not change
  // within a page view, so this runs once (twice in dev StrictMode only).
  useEffect(() => {
    if (!enabled || !placeId) return
    let cancelled = false
    fetchGoogleRating(placeId).then((r) => {
      if (!cancelled) setValue(r)
    })
    return () => {
      cancelled = true
    }
  }, [placeId, enabled])
  return value
}
